import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  DashboardFacade,
  SharedFacade,
  CustomFacade,
  UserDetailsFacade,
  AuthFacade,
  delayedFunctionCall,
} from '@piqet-angular/data';
import {
  CustomModalService,
  GuideTourEnum,
  LicenseType,
  adminRoleStr,
  delayedGTSteps,
  guideTourSteps,
  orgPrefsReminder,
  reviewPrefsTxt,
  welcomeText,
} from '@piqet-angular/shared';
import { DashboardService } from 'libs/data/src/lib/services/dashboard/dashboard.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { Subscription, catchError, finalize, firstValueFrom, lastValueFrom, of } from 'rxjs';
import { ProjectsDataGridService } from '../projects/projects-data-grid.service';
import { ProjectsService } from 'libs/data/src/lib/services/projects/projects.service';
import { ToastrService } from 'ngx-toastr';
import { JoyrideService } from 'ngx-joyride';
import { SharedService } from 'libs/data/src/lib/services/shared/shared.service';
import { ViewportScroller } from '@angular/common';
import { SwalService } from 'libs/shared/src/lib/service/swal.service';
import { CookieService } from 'ngx-cookie-service';

@Component({
  selector: 'piqet-angular-dashboard',
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  routeToChange: any;
  email: any;
  typeSelected = 'line-spin-clockwise-fade';
  deleteProjectData: Subscription;
  copyProjectData: Subscription;
  activatedRoute: any;
  flagName: any;
  subscription!: Subscription;
  tourSteps = guideTourSteps;
  delayedGTSteps = delayedGTSteps;
  stepNumber: any;

  constructor(
    public dashboardFacade: DashboardFacade,
    public sharedFacade: SharedFacade,
    public customFacade: CustomFacade,
    public UserDetailsFacade: UserDetailsFacade,
    public dashboardService: DashboardService,
    public spinnerService: NgxSpinnerService,
    private route: ActivatedRoute,
    private modal: CustomModalService,
    private authFacade: AuthFacade,
    private projectsDataGridService: ProjectsDataGridService,
    private projectService: ProjectsService,
    private toaster: ToastrService,
    public joyrideService: JoyrideService,
    public sharedService: SharedService,
    private viewportScroller: ViewportScroller,
    private router: Router,
    private swalService: SwalService,
    private cookieService: CookieService
  ) {
    let isEmpty = true;
    this.route.queryParams.subscribe((data: any) => {
      for (const key in data) {
        if (data.hasOwnProperty(key)) {
          isEmpty = false;
          break;
        }
      }
      this.routeToChange = data['isFlag'] || data['haveTour'];
      this.email = data['email'];
      this.flagName = Boolean(data['haveTour']);
      if (this.flagName) {
        setTimeout(() => {
          this.startTour();
        }, 0);
      }
    });

    if (this.routeToChange || isEmpty) {
      this.dashboardFacade.GetDashboardData();
    }

    this.activatedRoute = this.route.snapshot.routeConfig?.path;

    this.deleteProjectData = this.projectsDataGridService.deleteProjectData$.subscribe(
      (deleteData: any) => {
        this.deletePackagingProject(deleteData[0]);
      }
    );

    this.copyProjectData = this.projectsDataGridService.copyProjectData$.subscribe(
      (copyData: any) => {
        this.copyProject(copyData[0], copyData[1]);
      }
    );
  }

  async getTourStatus() {
    let res = (await lastValueFrom(this.sharedService.getGuideTourStatus(GuideTourEnum.BasicTour)))
      ?.data;
    if (res != null && !res.messageType && res == false && !this.joyrideService.isTourInProgress())
      this.showGuidedTour();
  }

  ngOnInit() {
    this.viewportScroller.scrollToPosition([0, 0]);
    this.getTourStatus();
  }

  showGuidedTour() {
    this.swalService
      .confirm(welcomeText, 'To get you started, here is a tour of the tool.', '', true)
      .then(confirmed => {
        this.startTour();
      });
  }

  onNext() {}

  startTour() {
    this.joyrideService.closeTour();
    this.sharedService.startTourFun(true);
    const options = {
      steps: this.tourSteps,
      stepDefaultPosition: 'bottom',
      themeColor: '#9747FF',
      showPrevButton: true,
      logsEnabled: false,
      scrollIfStepAndTargetAreNotVisible: true,
      waitingTime: 100,
    };
    let tourCompleted = false;
    document.body.classList.add('guided-tour-active');
    this.joyrideService.startTour(options).subscribe(
      step => {
        this.stepNumber = step?.number;
        if (this.tourSteps.length === this.stepNumber) {
          tourCompleted = true;
        }

        if (this.delayedGTSteps.includes(step?.number)) {
          delayedFunctionCall(this.scrollToTop, step?.number == 14 ? 200 : 0, this); // Scroll to the top of the page before each step
        } else if (step.number == 8) {
          this.scrollToSpecific('proj-scope');
        } else if (step.number == 12) {
          this.scrollToSpecific('Description');
        } else if (step.number == 15) {
          this.scrollToSpecific('internal-sect');
        } else if (step.number == 16) {
          this.scrollToSpecific('pack-config');
        }
      },
      e => {},
      async () => {
        if (tourCompleted) return;
        document.body.classList.remove('guided-tour-active');
        const userType: any = this.cookieService.get('u_type');
        const redirectToPreference = JSON.parse(this.cookieService.get('redirectToPreference'));
        const licenseType = parseInt(sessionStorage.getItem('licenseType') || '0', 10);
        const tourName = localStorage.getItem('tourName');
        await firstValueFrom(
          this.sharedService.updateGuideTourStatus(GuideTourEnum.BasicTour, false, this.stepNumber)
        );
        if (
          tourName != 'manual' &&
          redirectToPreference &&
          this.sharedService.decodeURIComponentParam(userType)?.toLowerCase() == adminRoleStr &&
          licenseType != LicenseType.Trial
        )
          this.sharedService.showPreferencesPopup();
        this.sharedService.startTourFun(false);
        localStorage.removeItem('tourName');
      }
    );
  }

  scrollToTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }

  scrollToSpecific(eleId: any) {
    const ele = document.getElementById(eleId);
    if (ele) {
      const elementPosition = ele.getBoundingClientRect().top + window.scrollY;
      const scrollToPosition =
        eleId == 'internal-sect'
          ? elementPosition - 400
          : eleId == 'pack-config'
          ? elementPosition - 300
          : elementPosition - 200;
      setTimeout(() => {
        window.scrollTo({ top: scrollToPosition, behavior: 'smooth' });
      }, 0);
    }
  }

  updateAchievementsVisibility(event: boolean) {
    this.dashboardService.updateAchievementsVisibility(event).subscribe((res: any) => {
      if (res) {
        this.sharedFacade.successToaster(res.message);
      }
    });
  }

  deletePackagingProject(data: any) {
    this.projectService
      .deleteMyAllProjectList(data.projectId)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.dashboardFacade.GetDashboardData();
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  async copyProject(projectId: any, label: any) {
    const copyResponse = await this.projectService.getMyAllProjectModalDataForCopy(projectId);
    this.modal.invoke('newCopyMyProjectModal', {
      initialState: {
        newMyProjects: copyResponse.data,
        pathName: this.activatedRoute,
        label: label,
      },
      class: 'modal-xl',
    });
    this.modal.ref?.content.saveNewCopyMyProjectEvent.subscribe(
      (saveNewCopyMyProjectEvent: any) => {
        this.copyPackagingProject(saveNewCopyMyProjectEvent);
      }
    );
  }

  copyPackagingProject(data: any) {
    this.projectService
      .copyMyAllProjectData(data)
      .pipe(
        catchError((error: any) =>
          of(this.toaster.error('An error occurred. Please try again later.'))
        ),
        finalize(() => {
          this.dashboardFacade.GetDashboardData();
          this.modal.close();
        })
      )
      .subscribe(res => {
        res.messageType ? this.toaster.error(res.message) : this.toaster.success(res.message);
      });
  }

  ngOnDestroy() {
    this.deleteProjectData.unsubscribe();
    this.copyProjectData.unsubscribe();
  }
}
